import { DataSource, CollectionViewer } from "@angular/cdk/collections";
import { BehaviorSubject, Subscription, Observable } from "rxjs";
import { Config } from "src/infrastructure/config";
import { ProductSearchFilter } from "src/models/DTO/filters/productSearchFilter";
import { Group } from "src/models/DTO/groups/group";
import { GroupCollectionItem } from "src/models/DTO/groups/groupCollectionItem";
import { GroupsService } from "src/services/request/groups/groups.service";
import { ImagesService } from "src/services/request/images/images.service";
import { ProductService } from "src/services/request/product/product.service";

export class AllGroupsDataSource extends DataSource<GroupCollectionItem | undefined> {
    private _length = Config.defaultItemsPerPage;
    private _lastPage: number = -1;
    private _groupsLength = 0;
    private _filters: ProductSearchFilter;
    private _query: string;
    private _sorter: string;
    private _pageSize = Config.defaultItemsPerPage;
    private _cachedData: (GroupCollectionItem | undefined)[]
    private _fetchedPages = new Set<number>();
    private readonly _dataStream;
    private readonly _subscription = new Subscription();
    private virtualScroll?: any;
    private _loading = false;

    constructor(
      private groupService: GroupsService,
      private imagesService: ImagesService,
      private productService: ProductService,
      lastPage: number,
      query: string,
      sorter: string,
      filters: ProductSearchFilter,
      pageSize: number) {
      super();
      this._lastPage = lastPage;
      this._filters = filters;
      this._query = query;
      this._sorter = sorter;
      this._pageSize = pageSize;
      this._length = pageSize;
      this._cachedData = Array.from<GroupCollectionItem | undefined>({length: this._length});
      this._dataStream = new BehaviorSubject<(GroupCollectionItem | undefined)[]>([]);

    }

    connect(collectionViewer: CollectionViewer): Observable<(GroupCollectionItem | undefined)[]> {
      this.virtualScroll = collectionViewer;
      this._fetchPage(0);
      this._subscription.add(
        collectionViewer.viewChange.subscribe(range => {
          const startPage = this._getPageForIndex(range.start);
          const endPage = this._getPageForIndex(range.end - 1);
          for (let i = startPage; i <= endPage; i++) {
            this._fetchPage(i);
          }
        }),
      );
      return this._dataStream;
    }
  
    disconnect(): void {
      this._subscription.unsubscribe();
    }

    get groupsLength() {
      return this._groupsLength;
    }

    set groupsLength(value) {
      this._groupsLength = value;
    }

    get items() {
      return this._cachedData;
    }

    get loading() {
      return this._loading;
    }
  
    private _getPageForIndex(index: number): number {
      return Math.floor(index / this._pageSize);
    }
  
    private _fetchPage(pageToLoad: number) {
      const page = pageToLoad;
      const pageFromServer = pageToLoad + 1; 
      if (this._fetchedPages.has(page)) {
        return;
      }
      if (this._lastPage < pageFromServer) {
        return;
      }
      this._fetchedPages.add(page);
      this._loading = true;
      let promises: Array<Promise<void>> = []
      this.groupService.getFilteredGroups(this._pageSize, pageFromServer, this._query, this._sorter, this._filters).subscribe(groups => {
        this._groupsLength += groups.data.length;
        this._loading = false
        groups.data.forEach(group => {
          group.imageURL = '';
          let imagePromise = new Promise<void>((resolve, reject) => {
            this.imagesService.getImagesForProduct("PRODUCT", group.product.uuid).subscribe(images => {
              group.imageURL = images.data[0].original.url
              resolve();
            })
          })
          let productPromise = new Promise<void>((resolve, reject) => {
            this.productService.getProductInfo(group.product.uuid).subscribe(productInfo => {
              group.productInfo = productInfo;
              group.priceForOne = (group.productInfo!.data.pricing!.find(pricing => pricing.quantity_to == 1)?.price_per_piece || '');
              let nextQuantity = group.quantity_reserved + 1;
              group.nextPrice = (
                group.productInfo!.data.pricing!.find(pricing => pricing.quantity_to >= nextQuantity && pricing.quantity_from <= nextQuantity)?.price_per_piece 
                || group.productInfo!.data.pricing.find(pricing => pricing.quantity_to == Math.max(...group.productInfo!.data.pricing!.map(pricing => pricing.quantity_to)))?.price_per_piece)
                resolve()
            })
          })
          promises.push(imagePromise);
          promises.push(productPromise);
          this.updateExpirationDate(group);
        })
        Promise.all(promises).then(() => {
          this._cachedData.splice(
            page * this._pageSize,
            this._pageSize,
            ...groups.data
          );
          this._dataStream.next(this._cachedData);
          this.virtualScroll!._viewport.checkViewportSize()
        })
      })
    }

    public updateExpirationDate(group: GroupCollectionItem) {
      var diffMs = this.compareDates(group.expires_at);
      let seconds = Math.ceil(diffMs);
      // 2- Extract hours:
      const hours = Math.floor( seconds / 3600 ); // 3,600 seconds in 1 hour
      seconds = seconds % 3600; // seconds remaining after extracting hours
      // 3- Extract minutes:
      const minutes = Math.floor( seconds / 60 ); // 60 seconds in 1 minute
      // 4- Keep only seconds not extracted to minutes:
      seconds = seconds % 60;
      group.expiration = {
        hours: this.appendZerosToTime(hours) + ' ч.',
        minutes: this.appendZerosToTime(minutes) + ' м.',
      }
    }

    private appendZerosToTime(value: number) {
      if (value < 10) {
        return '0' + value;
      }
      return value.toString();
    }

    private compareDates(start: string) {
      let parsedDate = start.split(/[- :]/);
      var startDate = new Date(parseInt(parsedDate[0]), parseInt(parsedDate[1])-1,
                               parseInt(parsedDate[2]), parseInt(parsedDate[3]), 
                               parseInt(parsedDate[4]), parseInt(parsedDate[5]));
      // Do your operations
      var endDate   = new Date().toLocaleString("en-US", {timeZone: "Europe/Sofia"});
      var seconds = (startDate.getTime() - new Date(endDate).getTime()) / 1000;
      return seconds;
    }

  }